import { useState, useCallback, useRef, useEffect } from "react";
import { useFuelData } from "@/hooks/use-fuel";
import { useFuelContext } from "@/hooks/use-fuel-context";
import { useSearchSettings } from "@/hooks/use-search-settings";
import { FuelSelector } from "@/components/FuelSelector";
import { SearchSettingsDialog } from "@/components/SearchSettingsDialog";
import { getVehiclePreset } from "@/types/settings";
import {
  findStationsAlongRoute,
  type RouteStation,
} from "@/lib/routeSearch";
import { formatCurrency } from "@/lib/tripCost";
import {
  ArrowDownUp,
  Search,
  Fuel,
  MapPin,
  Clock,
  Route,
  AlertTriangle,
  Loader2,
  Award,
  TrendingDown,
  Navigation,
  Map as MapIcon,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  loadGoogleMaps,
  getMapStyleOptions,
  createMarker,
  onMarkerClick,
  removeMarker,
  openInfoWindowAt,
  triggerMarkerClick,
  getMarkerPosition,
  type AnyMarker,
} from "@/lib/google-maps";

// ---------------------------------------------------------------------------
// Google Maps script loader
// ---------------------------------------------------------------------------

function useGoogleMaps() {
  const [ready, setReady] = useState(
    typeof window !== "undefined" && !!window.google?.maps?.Map,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ready) return;
    loadGoogleMaps()
      .then(() => setReady(true))
      .catch(() => setError("Unable to load maps. Please check your connection and try again."));
  }, [ready]);

  return { ready, error };
}

// ---------------------------------------------------------------------------
// Route-specific SVG helpers
// ---------------------------------------------------------------------------

function priceMarkerSvg(price: number, color: string): { svgHtml: string; width: number } {
  const text = price.toFixed(1);
  const width = text.length * 9 + 20;
  const svgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="28" viewBox="0 0 ${width} 28">
    <rect x="1" y="1" width="${width - 2}" height="26" rx="13" fill="${color}" stroke="#111" stroke-width="1.5"/>
    <text x="${width / 2}" y="19" text-anchor="middle" font-size="13" font-weight="700" fill="#fff" font-family="system-ui,sans-serif">${text}</text>
  </svg>`;
  return { svgHtml, width };
}

function getPriceColor(price: number, allPrices: number[]): string {
  if (allPrices.length === 0) return "#eab308";
  const sorted = [...allPrices].sort((a, b) => a - b);
  const q25 = sorted[Math.floor(sorted.length * 0.25)];
  const q75 = sorted[Math.floor(sorted.length * 0.75)];
  if (price <= q25) return "#22c55e";
  if (price >= q75) return "#ef4444";
  return "#eab308";
}

// ---------------------------------------------------------------------------
// RouteMap component
// ---------------------------------------------------------------------------

interface RouteMapProps {
  directionsResult: google.maps.DirectionsResult | null;
  stations: RouteStation[];
  selectedFuel: string;
  highlightStationCode?: string | null;
}

function RouteMap({ directionsResult, stations, selectedFuel, highlightStationCode }: RouteMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const markersRef = useRef<AnyMarker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    if (!window.google?.maps) return;

    mapInstance.current = new google.maps.Map(mapRef.current, {
      center: { lat: -33.8688, lng: 151.2093 },
      zoom: 11,
      ...getMapStyleOptions(),
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM },
      backgroundColor: "#1a1a2e",
    });

    infoWindowRef.current = new google.maps.InfoWindow();
  }, []);

  // Render directions
  const routeMarkersRef = useRef<AnyMarker[]>([]);

  useEffect(() => {
    if (!mapInstance.current) return;

    if (rendererRef.current) {
      rendererRef.current.setMap(null);
      rendererRef.current = null;
    }
    routeMarkersRef.current.forEach((m) => removeMarker(m));
    routeMarkersRef.current = [];

    if (!directionsResult) return;

    rendererRef.current = new google.maps.DirectionsRenderer({
      map: mapInstance.current,
      directions: directionsResult,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#f97316",
        strokeOpacity: 0.8,
        strokeWeight: 5,
      },
    });

    // Custom origin/destination markers with styled InfoWindows
    const leg = directionsResult.routes[0]?.legs[0];
    if (leg) {
      const waypoints = [
        { position: leg.start_location, label: "A", title: "Start", address: leg.start_address },
        { position: leg.end_location, label: "B", title: "Destination", address: leg.end_address },
      ];

      waypoints.forEach((wp) => {
        const svgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="14" fill="#f97316" stroke="#fff" stroke-width="2"/>
          <text x="16" y="21" text-anchor="middle" font-size="14" font-weight="bold" fill="#fff" font-family="system-ui">${wp.label}</text>
        </svg>`;

        const marker = createMarker({
          position: wp.position,
          map: mapInstance.current!,
          svgHtml,
          width: 32,
          height: 32,
          title: wp.title,
        });

        onMarkerClick(marker, () => {
          if (!infoWindowRef.current || !mapInstance.current) return;
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(wp.address)}`;
          infoWindowRef.current.setContent(
            `<div style="color:#e2e2e2;padding:10px 12px;padding-right:28px;font-family:system-ui,sans-serif;min-width:160px;position:relative">
              <button onclick="this.closest('.gm-style-iw-a')?.querySelector('button[aria-label]')?.click()" style="position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:50%;border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.08);color:#9ca3af;font-size:14px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0" aria-label="Close">✕</button>
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                <span style="display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#f97316;color:#fff;font-weight:700;font-size:12px">${wp.label}</span>
                <span style="font-weight:700;font-size:13px">${wp.title}</span>
              </div>
              <div style="font-size:11px;color:#9ca3af;margin-bottom:8px;line-height:1.4">${wp.address}</div>
              <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:4px;padding:5px 10px;background:rgba(249,115,22,0.2);border:1px solid rgba(249,115,22,0.4);border-radius:6px;color:#f97316;font-size:11px;font-weight:600;text-decoration:none">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8c0 4.5-6 11-6 11s-6-6.5-6-11a6 6 0 0 1 12 0z"/><circle cx="12" cy="8" r="2"/></svg>
                Open in Maps
              </a>
            </div>`
          );
          openInfoWindowAt(infoWindowRef.current, mapInstance.current, marker);
        });

        routeMarkersRef.current.push(marker);
      });
    }
  }, [directionsResult]);

  // Render station markers
  useEffect(() => {
    if (!mapInstance.current) return;

    markersRef.current.forEach((m) => removeMarker(m));
    markersRef.current = [];

    if (stations.length === 0) return;

    const allPrices = stations.map((rs) => rs.price);

    stations.forEach((rs) => {
      const color = getPriceColor(rs.price, allPrices);
      const { svgHtml, width: markerWidth } = priceMarkerSvg(rs.price, color);

      const marker = createMarker({
        position: rs.station.location,
        map: mapInstance.current!,
        svgHtml,
        width: markerWidth,
        height: 28,
        title: `${rs.station.name} - ${rs.price.toFixed(1)}¢`,
        zIndex: rs.price <= allPrices[Math.floor(allPrices.length * 0.25)] ? 100 : 10,
      });

      onMarkerClick(marker, () => {
        if (!infoWindowRef.current || !mapInstance.current) return;
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${rs.station.location.lat},${rs.station.location.lng}`;
        infoWindowRef.current.setContent(
          `<div style="color:#e2e2e2;padding:10px 12px;padding-right:28px;font-family:system-ui,sans-serif;min-width:150px;position:relative">
            <button onclick="this.closest('.gm-style-iw-a')?.querySelector('button[aria-label]')?.click()" style="position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:50%;border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.08);color:#9ca3af;font-size:14px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0" aria-label="Close">✕</button>
            <div style="font-weight:700;font-size:13px;margin-bottom:2px">${rs.station.name}</div>
            <div style="font-size:11px;color:#9ca3af;margin-bottom:4px">${rs.station.brand}</div>
            <div style="font-size:20px;font-weight:800;font-family:monospace;color:#fff;margin-bottom:4px">${rs.price.toFixed(1)} ¢/L</div>
            <div style="font-size:10px;color:#6b7280;margin-bottom:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px">${rs.station.address}</div>
            <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:4px;padding:5px 10px;background:rgba(249,115,22,0.2);border:1px solid rgba(249,115,22,0.4);border-radius:6px;color:#f97316;font-size:11px;font-weight:600;text-decoration:none">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8c0 4.5-6 11-6 11s-6-6.5-6-11a6 6 0 0 1 12 0z"/><circle cx="12" cy="8" r="2"/></svg>
              Open in Maps
            </a>
          </div>`
        );
        openInfoWindowAt(infoWindowRef.current, mapInstance.current, marker);
      });

      markersRef.current.push(marker);
    });
  }, [stations, selectedFuel]);

  // Highlight a station when selected from the list
  useEffect(() => {
    if (!highlightStationCode || !mapInstance.current || markersRef.current.length === 0) return;
    const idx = stations.findIndex((rs) => rs.station.code === highlightStationCode);
    if (idx === -1) return;
    const marker = markersRef.current[idx];
    if (!marker) return;

    const pos = getMarkerPosition(marker);
    if (pos) {
      mapInstance.current.panTo(pos);
      mapInstance.current.setZoom(15);
    }
    // Trigger the marker click to open InfoWindow
    triggerMarkerClick(marker);
  }, [highlightStationCode, stations]);

  return <div ref={mapRef} className="w-full h-full" />;
}

// ---------------------------------------------------------------------------
// Route Page
// ---------------------------------------------------------------------------

interface RouteInfo {
  distanceText: string;
  durationText: string;
  distanceKm: number;
}

export default function RoutePage() {
  const { data: fuelData, isLoading: fuelLoading } = useFuelData();
  const { selectedFuel, setSelectedFuel } = useFuelContext();
  const {
    vehicleType, tankSize, customConsumption,
    sortBy, radius,
    setSortBy, setRadius, setVehicleType,
    resetSettings,
  } = useSearchSettings();
  const { ready: mapsReady, error: mapsError } = useGoogleMaps();

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [stations, setStations] = useState<RouteStation[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [directionsResult, setDirectionsResult] = useState<google.maps.DirectionsResult | null>(null);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [focusedStationCode, setFocusedStationCode] = useState<string | null>(null);

  const originRef = useRef<HTMLInputElement>(null);
  const destinationRef = useRef<HTMLInputElement>(null);

  // Wire up Places Autocomplete when Maps JS is ready
  useEffect(() => {
    if (!mapsReady || !google.maps.places) return;
    const opts: google.maps.places.AutocompleteOptions = {
      componentRestrictions: { country: "au" },
      fields: ["formatted_address"],
    };
    if (originRef.current && !(originRef.current as any)._autocomplete) {
      const ac = new google.maps.places.Autocomplete(originRef.current, opts);
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place.formatted_address) setOrigin(place.formatted_address);
      });
      (originRef.current as any)._autocomplete = ac;
    }
    if (destinationRef.current && !(destinationRef.current as any)._autocomplete) {
      const ac = new google.maps.places.Autocomplete(destinationRef.current, opts);
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place.formatted_address) setDestination(place.formatted_address);
      });
      (destinationRef.current as any)._autocomplete = ac;
    }
  }, [mapsReady]);

  const swap = useCallback(() => {
    setOrigin((prev) => {
      setDestination((d) => {
        // swap both at once
        setOrigin(d);
        return prev;
      });
      return prev; // will be overwritten
    });
    // simpler approach
    const o = origin;
    setOrigin(destination);
    setDestination(o);
  }, [origin, destination]);

  const search = useCallback(async () => {
    if (!origin.trim() || !destination.trim()) {
      setError("Please enter both an origin and a destination.");
      return;
    }
    if (!mapsReady) {
      setError("Google Maps is still loading. Please wait a moment.");
      return;
    }
    if (!fuelData?.stations) {
      setError("Fuel data is still loading. Please wait a moment.");
      return;
    }

    setLoading(true);
    setError(null);
    setStations([]);
    setRouteInfo(null);
    setDirectionsResult(null);
    setHasSearched(true);

    try {
      const directionsService = new google.maps.DirectionsService();
      const result = await directionsService.route({
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
      });

      const route = result.routes[0];
      if (!route) {
        setError("No driving route found between those locations.");
        return;
      }

      const leg = route.legs[0];
      const info: RouteInfo = {
        distanceText: leg.distance?.text ?? "Unknown",
        durationText: leg.duration?.text ?? "Unknown",
        distanceKm: (leg.distance?.value ?? 0) / 1000,
      };
      setRouteInfo(info);
      setDirectionsResult(result);

      const polyline = route.overview_polyline;
      const allStations = Object.values(fuelData.stations);

      const found = findStationsAlongRoute({
        routePolyline: polyline,
        stations: allStations,
        fuelType: selectedFuel,
        bufferKm: 2,
        limit: 20,
        vehicleType,
        customConsumption,
      });

      setStations(found);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Route search failed.";
      if (msg.includes("ZERO_RESULTS")) {
        setError("No driving route found between those locations.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [
    origin,
    destination,
    mapsReady,
    fuelData,
    selectedFuel,
    vehicleType,
    customConsumption,
  ]);

  const vehiclePreset = getVehiclePreset(vehicleType);
  const worstCost =
    stations.length > 0
      ? stations[stations.length - 1].totalTripCost
      : 0;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="bg-background font-sans">
      <div className="max-w-7xl mx-auto px-4 pt-2 pb-6">
        {/* Google Maps error banner */}
        {mapsError && (
          <div className="mb-4 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {mapsError}
          </div>
        )}

        {/* Two-column layout on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT — inputs + results list */}
          <div className="space-y-4">
            {/* Fuel selector */}
            <FuelSelector
              selected={selectedFuel}
              onSelect={setSelectedFuel}
            />

            {/* Origin / Destination inputs */}
            <div className="bg-card border border-white/5 rounded-2xl p-4 space-y-3">
              {/* Header row: vehicle info + settings button */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>{vehiclePreset.icon}</span>
                  {vehiclePreset.name} · {customConsumption ?? vehiclePreset.consumption} L/100km · {tankSize}L
                </span>
                <SearchSettingsDialog
                  sortBy={sortBy}
                  radius={radius}
                  vehicleType={vehicleType}
                  onSortByChange={setSortBy}
                  onRadiusChange={setRadius}
                  onVehicleTypeChange={setVehicleType}
                  onReset={resetSettings}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  From
                </label>
                <input
                  ref={originRef}
                  type="text"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="Starting point (e.g. 2000 or address)"
                  className="w-full bg-secondary/40 border border-white/5 rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Swap button */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={swap}
                  className="p-2 rounded-full bg-secondary/60 border border-white/10 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  aria-label="Swap origin and destination"
                >
                  <ArrowDownUp size={16} />
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  To
                </label>
                <input
                  ref={destinationRef}
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="Destination (e.g. 2010 or address)"
                  className="w-full bg-secondary/40 border border-white/5 rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Search button */}
              <button
                type="button"
                onClick={search}
                disabled={loading || !mapsReady || fuelLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-4 py-3 text-sm font-bold transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Search size={16} />
                )}
                {loading ? "Searching…" : "Find Stations"}
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
                <AlertTriangle size={16} />
                {error}
              </div>
            )}

            {/* Route summary */}
            {routeInfo && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-white/5 rounded-2xl p-4 flex items-center gap-4 text-sm"
              >
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Route size={14} />
                  <span className="font-medium text-foreground">
                    {routeInfo.distanceText}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock size={14} />
                  <span className="font-medium text-foreground">
                    {routeInfo.durationText}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Fuel size={14} />
                  <span className="font-medium text-foreground">
                    {stations.length} station{stations.length !== 1 && "s"}{" "}
                    found
                  </span>
                </div>
              </motion.div>
            )}

            {/* Mobile map toggle + map */}
            {directionsResult && (
              <div className="lg:hidden">
                <button
                  type="button"
                  onClick={() => setShowMobileMap((v) => !v)}
                  className="w-full flex items-center justify-center gap-2 bg-secondary/40 border border-white/5 rounded-xl px-4 py-2.5 text-sm font-medium text-foreground hover:bg-secondary/60 transition-colors"
                >
                  {showMobileMap ? <X size={16} /> : <MapIcon size={16} />}
                  {showMobileMap ? "Hide Map" : "Show Route Map"}
                </button>
                {showMobileMap && (
                  <div className="mt-3 rounded-2xl overflow-hidden border border-white/5 h-[300px]">
                    <RouteMap
                      directionsResult={directionsResult}
                      stations={stations}
                      selectedFuel={selectedFuel}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Station list */}
            <AnimatePresence mode="wait">
              {!hasSearched && !loading && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-card border border-white/5 rounded-2xl p-8 text-center"
                >
                  <div className="text-5xl mb-4">🛣️</div>
                  <h2 className="text-lg font-bold mb-2">
                    Find Stations on Your Route
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Enter your origin and destination to discover the cheapest
                    fuel stops along the way.
                  </p>
                </motion.div>
              )}

              {hasSearched && !loading && stations.length === 0 && !error && (
                <motion.div
                  key="no-results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-card border border-white/5 rounded-2xl p-8 text-center"
                >
                  <div className="text-5xl mb-4">😕</div>
                  <h2 className="text-lg font-bold mb-2">No Stations Found</h2>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    No {selectedFuel} stations were found within 2 km of this
                    route. Try a different fuel type or route.
                  </p>
                </motion.div>
              )}

              {stations.length > 0 && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                >
                  {stations.map((rs, idx) => {
                    const isBest = idx === 0;
                    const savings = worstCost - rs.totalTripCost;

                    return (
                      <motion.div
                        key={rs.station.code}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.04 }}
                        onClick={() => setFocusedStationCode(rs.station.code)}
                        className={`bg-card border rounded-2xl p-4 transition-colors cursor-pointer hover:border-primary/30 ${
                          focusedStationCode === rs.station.code
                            ? "border-primary/50 ring-1 ring-primary/20"
                            : isBest
                              ? "border-emerald-500/50 ring-1 ring-emerald-500/20"
                              : "border-white/5"
                        }`}
                      >
                        {/* Best badge */}
                        {isBest && (
                          <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-bold mb-2">
                            <Award size={14} />
                            Best Value on Route
                          </div>
                        )}

                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-sm truncate">
                              {rs.station.name}
                            </h3>
                            <p className="text-xs text-muted-foreground truncate">
                              {rs.station.brand} · {rs.station.address}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-lg font-bold font-mono">
                              {(rs.price / 100).toFixed(1)}
                              <span className="text-xs text-muted-foreground ml-0.5">
                                ¢/L
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Metadata row */}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Navigation size={12} />
                            {rs.detourKm === 0
                              ? "On route"
                              : `${rs.detourKm}km off route`}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={12} />
                            {rs.distanceAlongRoute}km from start
                          </span>
                          <span className="flex items-center gap-1">
                            <Fuel size={12} />
                            Fill + detour:{" "}
                            <span className="text-foreground font-medium">
                              {formatCurrency(rs.totalTripCost)}
                            </span>
                          </span>
                          {savings > 0.01 && (
                            <span className="flex items-center gap-1 text-emerald-400">
                              <TrendingDown size={12} />
                              Save {formatCurrency(savings)} vs worst
                            </span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT — route map (desktop) */}
          <div className="hidden lg:block">
            <div className="bg-card border border-white/5 rounded-2xl sticky top-24 min-h-[400px] h-[calc(100vh-8rem)] overflow-hidden">
              {directionsResult ? (
                <RouteMap
                  directionsResult={directionsResult}
                  stations={stations}
                  selectedFuel={selectedFuel}
                  highlightStationCode={focusedStationCode}
                />
              ) : (
                <RouteMap
                  directionsResult={null}
                  stations={[]}
                  selectedFuel={selectedFuel}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
